// React
import React from 'react';

//React Router
import { useNavigate } from 'react-router-dom';

// Mantine
import {
  Badge,
  Flex,
  Group,
  Skeleton,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';

// Utils
import { formatDateOnly } from 'utils/dateUtils';

// Custom Components
import { StyledPaper } from 'components/common/StyledPaper';

interface RemittanceInfo {
  filename: string;
  parsedAt: string;
  totalFeeAssessed: string | number | null;
  claimCount: number;
}

interface RemittanceCardProps {
  remittance: RemittanceInfo | null;
  icon: React.ComponentType<any>;
  loading: boolean;
}

export const RemittanceCard: React.FC<RemittanceCardProps> = ({
  remittance,
  icon: Icon,
  loading,
}) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('dark');
  const isDark = colorScheme === 'dark';
  const subtleText = isDark ? theme.colors.gray[5] : theme.colors.gray[6];
  const navigate = useNavigate();

  return (
    <StyledPaper testId="remittance-card" isLoading={loading}>
      <Flex
        direction="column"
        gap="md"
        onClick={() => navigate('/remittances')}
        style={{ cursor: 'pointer' }}
      >
        {/* Header */}
        <Group justify="space-between" align="center">
          <div>
            <Text fw={600} fz="sm" c={subtleText} tt="uppercase" lts={0.8}>
              Latest
            </Text>
            <Stack gap={6} mt={4}>
              <Text fz="xl" fw={700}>
                Remittance
              </Text>
              {remittance && (
                <Badge
                  radius="xl"
                  variant={isDark ? 'light' : 'outline'}
                  size="sm"
                  color="blue"
                >
                  Ingested
                </Badge>
              )}
            </Stack>
          </div>

          <Flex
            align="center"
            justify="center"
            w={40}
            h={40}
            style={{
              borderRadius: '999px',
              background: isDark
                ? 'rgba(148, 163, 184, 0.16)'
                : 'rgba(15, 23, 42, 0.06)',
            }}
          >
            <Icon
              size="1.4rem"
              stroke={2.2}
              style={{
                color: isDark ? theme.colors.gray[2] : theme.colors.dark[6],
              }}
            />
          </Flex>
        </Group>

        {/* Body */}
        {loading ? (
          <Stack data-testid="metric-card-skeleton" gap={'sm'}>
            <Skeleton height={18} />
            <Skeleton height={18} />
            <Skeleton height={18} />
            <Skeleton height={18} />
          </Stack>
        ) : (
          <Stack gap={6} mt={4} px={2}>
            {remittance ? (
              <>
                {remittance.filename && (
                  <Text size="sm" fw={600} truncate="end">
                    {remittance.filename}
                  </Text>
                )}

                <Group gap={6} wrap="wrap">
                  <Text size="xs" c={subtleText}>
                    Received On:
                  </Text>
                  <Text size="xs" fw={500}>
                    {formatDateOnly(remittance.parsedAt, { fallback: 'N/A' })}
                  </Text>
                </Group>

                {remittance.claimCount != null && (
                  <Group gap={6} wrap="wrap">
                    <Text size="xs" c={subtleText}>
                      Claims:
                    </Text>
                    <Text size="sm" fw={500}>
                      {remittance.claimCount}
                    </Text>
                  </Group>
                )}

                {remittance.totalFeeAssessed != null && (
                  <Group gap={6} wrap="wrap">
                    <Text size="xs" c={subtleText}>
                      Total Paid:
                    </Text>
                    <Text size="sm" fw={600}>
                      ${remittance.totalFeeAssessed}
                    </Text>
                  </Group>
                )}
              </>
            ) : (
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  No remittance data
                </Text>
                <Text size="xs" c={subtleText}>
                  Once a remittance is parsed, it will appear here.
                </Text>
              </Stack>
            )}
          </Stack>
        )}
      </Flex>
    </StyledPaper>
  );
};
